import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";

interface LawyerCustomizations {
  [key: string]: unknown;
}

export function usePatchLawyerCustomizations(buildPatch: () => Partial<LawyerCustomizations>) {
  const { user, authUser } = useAppContext();
  const queryClient = useQueryClient();

  const customizations = (user?.customizations as LawyerCustomizations | null) ?? {};
  const userQueryKey = ["app-user-data", authUser?.id] as const;

  return useMutation({
    mutationFn: async () => {
      if (!user?.user_id) return;
      const nextCustomizations = { ...customizations, ...buildPatch() };
      const { error } = await supabase
        .from("users")
        .update({ customizations: nextCustomizations })
        .eq("user_id", user.user_id);
      if (error) throw error;
    },
    onMutate: async () => {
      if (!user) return;
      await queryClient.cancelQueries({ queryKey: userQueryKey });
      const previous = queryClient.getQueryData(userQueryKey);
      queryClient.setQueryData(
        userQueryKey,
        (data: { user: typeof user; person: unknown } | undefined) => {
          if (!data?.user) return data;
          const prevCustomizations =
            (data.user.customizations as LawyerCustomizations | null) ?? {};
          return {
            ...data,
            user: {
              ...data.user,
              customizations: { ...prevCustomizations, ...buildPatch() },
            },
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(userQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });
}
